import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Zap, Trophy, Calendar, Play, TrendingUp, Sparkles } from "lucide-react";
import ConsistencyPanel from "../../components/ConsistencyPanel";

const READINESS_COLORS = {
  READY: "#4ade80", MODERATE: "#facc15", LOW: "#fb923c", REVIEW_REQUIRED: "#f87171",
};

export default function ClientDashboardV2() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/dashboard-v2").then(({ data }) => setData(data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  if (!data) return <AppShell><div style={{ padding: 40, color: "#a3a3a3" }}>Cargando…</div></AppShell>;

  const lastReady = data.readiness_recent?.[0];

  return (
    <AppShell>
      {/* Hero */}
      <div className="qv-hero-fade-in" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 6 }}>QV Performance</div>
        <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "clamp(2.4rem, 5vw, 3.4rem)", letterSpacing: "0.04em", margin: 0, lineHeight: 0.95 }}>
          Hoy es tu <span style={{ color: "#dc2626" }}>día</span>.
        </h1>
      </div>

      {/* Cards principales */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginBottom: 24 }}>
        {/* Readiness */}
        <div className="qv-card-anim" style={{ background: "#0a0a0a", border: `1px solid ${lastReady ? READINESS_COLORS[lastReady.state] + "55" : "rgba(255,255,255,0.06)"}`, padding: 18, borderRadius: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", display: "flex", alignItems: "center", gap: 6 }}>
              <Sparkles size={13}/> Readiness
            </div>
            {lastReady && <span style={{ fontSize: 10, letterSpacing: "0.14em", color: READINESS_COLORS[lastReady.state], fontWeight: 700 }}>{lastReady.state}</span>}
          </div>
          {lastReady ? (
            <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 48, color: READINESS_COLORS[lastReady.state], lineHeight: 1 }}>{lastReady.score}<span style={{ fontSize: 14, color: "#a3a3a3" }}>/100</span></div>
          ) : (
            <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 6 }}>Sin check-in hoy.</div>
          )}
          <Link to="/app/readiness" className="qv-link" style={{ display: "inline-block", marginTop: 10, fontSize: 12, color: "#dc2626", textDecoration: "none", letterSpacing: "0.08em" }}>
            {lastReady ? "Actualizar →" : "Empezar check-in →"}
          </Link>
        </div>

        {/* Próximo partido */}
        <div className="qv-card-anim" style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 18, borderRadius: 4 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <Calendar size={13}/> Próximo partido
          </div>
          {data.next_match ? (
            <>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 26, letterSpacing: "0.06em", marginTop: 4 }}>vs {data.next_match.opponent || "—"}</div>
              <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 4 }}>{data.next_match.date} · {data.next_match.kickoff_time || ""}</div>
            </>
          ) : <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 6 }}>Sin partido programado.</div>}
        </div>

        {/* Sesión hoy */}
        <div className="qv-card-anim" style={{ background: "linear-gradient(135deg, rgba(220,38,38,0.14), transparent)", border: "1px solid rgba(220,38,38,0.4)", padding: 18, borderRadius: 4 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#f87171", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <Zap size={13}/> Sesión de hoy
          </div>
          {data.next_session && data.next_session.date === data.today ? (
            <>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 24, letterSpacing: "0.04em" }}>{data.next_session.title || "Entrenamiento"}</div>
              <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 3 }}>{data.next_session.day_type}</div>
              <Link to={`/app/me/sessions/${data.next_session.id}`} data-testid="dashboard-start-session"
                    className="qv-btn-primary"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, background: "#dc2626", color: "#fff", padding: "10px 16px", fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", textDecoration: "none", borderRadius: 2 }}>
                <Play size={14}/> Empezar entrenamiento
              </Link>
            </>
          ) : data.next_session ? (
            <>
              <div style={{ fontSize: 13, color: "#a3a3a3" }}>Próxima sesión: <b style={{ color: "#e5e5e5" }}>{data.next_session.date}</b></div>
              <div style={{ fontSize: 14, marginTop: 4 }}>{data.next_session.title}</div>
            </>
          ) : <div style={{ fontSize: 13, color: "#a3a3a3" }}>Nada programado. Habla con tu coach.</div>}
        </div>
      </div>

      {/* Consistency & Streaks */}
      <ConsistencyPanel endpoint="/me/consistency" />

      {/* PBs */}
      {data.pbs && data.pbs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "#facc15", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
            <Trophy size={14}/> Personal Best
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            {data.pbs.map((pb) => (
              <div key={pb.protocol_key} className="qv-pb-card" style={{ background: "#0a0a0a", border: "1px solid rgba(250,204,21,0.2)", padding: 14, borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", color: "#a3a3a3", textTransform: "uppercase" }}>{pb.protocol_name}</div>
                <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, color: "#facc15", lineHeight: 1, marginTop: 4 }}>
                  {pb.best}<span style={{ fontSize: 12, color: "#a3a3a3", marginLeft: 4 }}>{pb.unit}</span>
                </div>
                <div style={{ fontSize: 10, color: "#737373", marginTop: 3 }}>{pb.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Readiness sparkline */}
      {data.readiness_recent && data.readiness_recent.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "#a3a3a3", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
            <TrendingUp size={14}/> Últimos días
          </div>
          <div style={{ display: "flex", gap: 3, height: 40, alignItems: "flex-end" }}>
            {[...data.readiness_recent].reverse().map((r) => (
              <div key={r.date} title={`${r.date} · ${r.state} · ${r.score}`}
                   style={{ flex: 1, background: READINESS_COLORS[r.state], height: `${r.score}%`, minHeight: 4, borderRadius: 1, transition: "height .3s ease" }} />
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
